import React, { useState, useEffect, useContext, useMemo } from "react";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import classnames from "classnames";
import { AppContext } from "@vahak/core/dist/app-context";

// Style
import faqStyle from "./Faq.module.scss";

// Component
import FaqHeader from "./FaqHeader/FaqHeader";
import A from "@vahak/core/dist/components/app-anchor/A";
import FaqQuestions from "@vahak/core/dist/components/FaqQuestions/FaqQuestions";
import PageMetaTags from "../common/page-meta-tags/PageMetaTags";

// Services
import { FaqService, FaqCateg } from "@vahak/core/dist/_services/faq.service";

// Icons
import Telephone from "@vahak/core/dist/icons/telephone.svg";

// Constants
import { FaqCategories, useFaqQuestions } from "@vahak/core/dist/constants/Faq";
import { NAV_MENU_STRINGS } from "@vahak/core/dist/constants/languages";
const GeneralCategory = { name: "General FAQ’s", slug: "general" };
interface ParamTypes {
    slug: string;
    locale: string;
}
const Faq = ({ slug, locale }: ParamTypes) => {
    const { t, i18n } = useTranslation(["faq"]);
    const router = useRouter();

    const [activeFaq, setActiveFaq] = useState<number | undefined>(undefined);
    const [isOpen, setIsOpen] = useState(true);
    const [faqCount, setFaqCount] = useState(0);
    const [showMoreCount, setShowMoreCount] = useState(5);
    const [selectedFaqCateg, setSelectedFaqCateg] = useState(GeneralCategory);

    const { isMobile } = useContext(AppContext);

    useEffect(() => {
        const urlSlug = router.asPath.split("/").length === 3 ? router.asPath.split("/")[2] : slug;
        const urlCategory = FaqCategories.filter((category) => category.slug === urlSlug);
        urlSlug && setSelectedFaqCateg(urlCategory[0] || GeneralCategory);
        const language = NAV_MENU_STRINGS.find((ele) =>
            ele.menu.includes("frequently-asked-questions")
        )?.languages?.find((language) => language.value === locale);

        language?.value && i18n.changeLanguage(language?.value);
        (async () => {
            const { count } = await FaqService.getFaqsByCateg({
                categSlug: urlSlug || selectedFaqCateg.slug
            });
            setFaqCount(count);
        })();
    }, []);

    const getAndSetData = async ({ categ }: { categ: FaqCateg }) => {
        router.push(`/frequently-asked-questions/${categ.slug}`);
    };

    const toggleActiveFaq = (val: any) => {
        const { currentFaqIndex, isOpen } = val;
        setActiveFaq(activeFaq === currentFaqIndex ? undefined : currentFaqIndex);
        setIsOpen(!isOpen);
    };

    const faqPath =
        selectedFaqCateg.slug === "general"
            ? "/frequently-asked-questions"
            : `/frequently-asked-questions/${selectedFaqCateg.slug}`;
    const canonicalUrl = `https://www.vahak.in${faqPath}`;
    const categoryName = FaqCategories.find((categ) => selectedFaqCateg.slug === categ.slug)?.name;
    const pageTitle =
        selectedFaqCateg.slug === "general" ? "Vahak - Frequently Asked Questions" : `Vahak - ${categoryName} FAQ`;
    const faqList = useMemo(
        () =>
            useFaqQuestions(t)
                .filter((faqQuestion) => faqQuestion.category === selectedFaqCateg.slug)
                .flatMap((data) => data.faqQuestions)
                .slice(0, showMoreCount)
                .map((item: { question?: string; response?: string }) => ({
                    question: item.question ?? "",
                    answer: (item.response ?? "").replace(/<[^>]*>/g, "")
                })),
        [t, selectedFaqCateg.slug, showMoreCount]
    );

    return (
        <>
            <PageMetaTags
                title={pageTitle}
                description="Get answers to all your questions regarding booking loads, finding truck load online, lorry booking and more. Go through our FAQ and answers."
                keywords="lorry booking,how to get loads for trucks,find truck loads online,book my truck,online truck load booking, vahak, vahak app"
                canonicalUrl={canonicalUrl}
                ogUrl={canonicalUrl}
                ogImgAltText="Book Trucks, Containers from Thousands of Trusted Transport businesses and Lorry Owners from All India"
                schemaOrgJsonUrl={faqPath}
                includeDefaultSchema={false}
                faqList={faqList}
            />
            <main>
                <FaqHeader
                    heading={t("faq:faq_heading")}
                    subHeading={t("faq:faq_subheading")}
                    handleCategorySelect={getAndSetData}
                    selectedFaqCateg={selectedFaqCateg}
                />
                <div className="container">
                    <div
                        className={classnames(faqStyle.main_layout, `${isMobile ? faqStyle.mobile : faqStyle.desktop}`)}
                    >
                        <h2>{FaqCategories.find((categ) => selectedFaqCateg.slug === categ.slug)?.name}</h2>
                        <div>
                            {useFaqQuestions(t)
                                .filter((faqQuestion) => faqQuestion.category === selectedFaqCateg.slug)
                                .map((data) =>
                                    data.faqQuestions
                                        .slice(0, showMoreCount)
                                        .map((faqQuestion: object, index: number) => (
                                            <FaqQuestions
                                                key={index}
                                                data={faqQuestion}
                                                index={index}
                                                handleClick={(val: any) => toggleActiveFaq(val)}
                                                isOpen={activeFaq === index ? !isOpen : false}
                                            />
                                        ))
                                )}
                        </div>
                        {showMoreCount >= 5 && faqCount > 5 && (
                            <div className={classnames(faqStyle.show_more)}>
                                <h3>
                                    <u
                                        className={classnames(faqStyle.link)}
                                        onClick={() =>
                                            showMoreCount > 5 ? setShowMoreCount(5) : setShowMoreCount(faqCount)
                                        }
                                    >
                                        {showMoreCount > 5 ? t("faq:show_less") : t("faq:show_more")}
                                    </u>
                                </h3>
                            </div>
                        )}
                        <div className={classnames(faqStyle.contact_us)}>
                            <div className={classnames(faqStyle.contact_us_wrapper)}>
                                <div className={classnames(faqStyle.contact_us_icon)}>
                                    <Telephone width="50" height="50" />
                                </div>
                                <div className={classnames(faqStyle.contact_us_content)}>
                                    <h3>
                                        {t("faq:if_you_cant_findthe_answer")}{" "}
                                        <A openInNewTab={false} href="/contact-us">
                                            <u>{t("faq:contact_us")}</u>
                                        </A>
                                    </h3>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

export default Faq;
